import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL;

const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`
});

// Meeting pages structure (exported for use in components)
export const MEETING_PAGES = [
  { id: 'agenda', name: 'Agenda', type: 'agenda' },
  { id: 'weekly_recap', name: 'Weekly Recap', type: 'recap' },
  { id: 'pillar_scope', name: 'Scope', type: 'pillar', pillar_id: 'scope' },
  { id: 'pillar_timeline', name: 'Timeline', type: 'pillar', pillar_id: 'timeline' },
  { id: 'pillar_implementation', name: 'Implementation', type: 'pillar', pillar_id: 'implementation' },
  { id: 'pillar_data_security', name: 'Data & Security', type: 'pillar', pillar_id: 'data_security' },
  { id: 'pillar_cost', name: 'Cost', type: 'pillar', pillar_id: 'cost' },
  { id: 'wrap_up', name: 'Meeting Recap', type: 'wrap_up' }
];

export const useMeetingManagement = (fetchData) => {
  const [activeMeeting, setActiveMeeting] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [showMeetingDialog, setShowMeetingDialog] = useState(false);
  const [meetingNotes, setMeetingNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState('note'); // note, action, decision, blocker, task

  // Start a new meeting
  const startMeeting = async (selectedPrepId) => {
    try {
      const response = await axios.post(
        `${API}/five-pillars-hub/meetings/start`,
        { title: `BIZOPS Weekly Update - ${format(new Date(), 'MMMM d, yyyy')}` },
        { headers: getHeaders() }
      );
      
      const meeting = response.data.meeting;
      
      // Load specific meeting prep if selected
      if (selectedPrepId) {
        try {
          const prepRes = await axios.get(`${API}/five-pillars-hub/meeting-prep`, { headers: getHeaders() });
          const preps = prepRes.data.preps || [];
          const prep = preps.find(p => p.id === selectedPrepId);
          if (prep) {
            meeting.agenda = prep.agenda || [];
            meeting.weekly_recap = {
              completed: prep.completed || [],
              issues: prep.issues || [],
              focus_this_week: prep.focus_this_week || [],
              last_meeting_notes: [],
              unfinished_from_last: []
            };
            meeting.presenter_notes = prep.presenter_notes || '';
            meeting.discussion_topics = prep.discussion_topics || [];
          }
        } catch (e) {
          console.log('Could not load meeting prep');
        }
      }

      // Auto-populate from last meeting's unfinished items
      try {
        const meetingsRes = await axios.get(
          `${API}/five-pillars-hub/meetings?limit=2`,
          { headers: getHeaders() }
        );
        const pastMeetings = (meetingsRes.data.meetings || []).filter(m => m.id !== meeting.id && m.status === 'completed');
        const lastMeeting = pastMeetings[0];
        
        if (lastMeeting) {
          // Pull unfinished action items and tasks from last meeting
          // Backend stores completed as boolean, but also check for status field for compatibility
          const unfinishedActions = (lastMeeting.action_items || [])
            .filter(a => !a.completed && a.status !== 'completed' && a.status !== 'done')
            .map(a => a.content || a.text || '');
          const unfinishedTasks = (lastMeeting.tasks || [])
            .filter(t => t.status !== 'completed' && t.status !== 'done')
            .map(t => t.content || t.text || t.title || '');
          
          // Also get unfinished items from notes (is_action_item or is_task with pending status)
          const unfinishedNoteActions = (lastMeeting.notes || [])
            .filter(n => (n.is_action_item || n.is_task) && n.status !== 'completed' && n.status !== 'done')
            .map(n => n.content || '');
          
          const lastNotes = (lastMeeting.notes || [])
            .filter(n => n.is_decision)
            .slice(-5)
            .map(n => n.content || n.text || '');
          
          // MERGE with prep data (don't overwrite)
          // Combine all unfinished items and deduplicate
          const allUnfinished = [...new Set([...unfinishedActions, ...unfinishedTasks, ...unfinishedNoteActions])].filter(Boolean);
          
          meeting.weekly_recap = {
            completed: meeting.weekly_recap?.completed || [],
            issues: meeting.weekly_recap?.issues || [],
            focus_this_week: meeting.weekly_recap?.focus_this_week || [],
            last_meeting_notes: [...lastNotes],
            unfinished_from_last: allUnfinished
          };
        }
      } catch (e) {
        console.log('Could not load last meeting data:', e);
      }
      
      // Initialize agenda if empty
      if (!meeting.agenda) meeting.agenda = [];
      if (!meeting.weekly_recap) {
        meeting.weekly_recap = { completed: [], issues: [], last_meeting_notes: [], focus_this_week: [], unfinished_from_last: [] };
      }
      
      setActiveMeeting(meeting);
      setShowMeetingDialog(false);
      toast.success('Meeting started!');
      
      // Save prep data to the meeting in backend
      if (meeting.agenda?.length > 0 || meeting.weekly_recap) {
        axios.patch(
          `${API}/five-pillars-hub/meetings/${meeting.id}/agenda`,
          { agenda: meeting.agenda || [] },
          { headers: getHeaders() }
        ).catch(() => {});
        if (meeting.weekly_recap) {
          axios.patch(
            `${API}/five-pillars-hub/meetings/${meeting.id}/weekly-recap`,
            meeting.weekly_recap,
            { headers: getHeaders() }
          ).catch(() => {});
        }
      }
    } catch (error) {
      console.error('Error starting meeting:', error);
      toast.error(error.response?.data?.detail || 'Failed to start meeting');
    }
  };

  // Navigate meeting pages
  const navigateMeeting = async (pageId) => {
    if (!activeMeeting) return;
    
    try {
      // Auto-save agenda and weekly recap before navigating
      const currentPage = MEETING_PAGES.find(p => p.id === activeMeeting.current_page);
      
      if (currentPage?.type === 'agenda' && activeMeeting.agenda) {
        await axios.patch(
          `${API}/five-pillars-hub/meetings/${activeMeeting.id}/agenda`,
          { agenda: activeMeeting.agenda },
          { headers: getHeaders() }
        ).catch(() => {});
      }
      
      if (currentPage?.type === 'recap' && activeMeeting.weekly_recap) {
        await axios.patch(
          `${API}/five-pillars-hub/meetings/${activeMeeting.id}/weekly-recap`,
          activeMeeting.weekly_recap,
          { headers: getHeaders() }
        ).catch(() => {});
      }
      
      await axios.patch(
        `${API}/five-pillars-hub/meetings/${activeMeeting.id}/navigate`,
        { page_id: pageId },
        { headers: getHeaders() }
      );
      
      setActiveMeeting(prev => ({ ...prev, current_page: pageId }));
    } catch (error) {
      console.error('Error navigating:', error);
    }
  };

  // Add meeting note
  const addMeetingNote = async () => {
    if (!activeMeeting || !newNote.trim()) return;
    
    const currentPage = MEETING_PAGES.find(p => p.id === activeMeeting.current_page);
    
    try {
      // If it's a task, create it in the main tasks system first
      if (noteType === 'task') {
        // Create the task in the main tasks collection
        const taskRes = await axios.post(
          `${API}/tasks`,
          {
            title: newNote,
            description: `Created from 5 Pillars meeting: ${activeMeeting.title}`,
            status: 'pending',
            priority: 'medium',
            tags: ['rhino-1', 'five_pillars'],
            source: 'five_pillars_meeting',
            project_id: 'rhino-1',
            meeting_id: activeMeeting.id
          },
          { headers: getHeaders() }
        );
        
        // Also record it in the meeting notes
        await axios.post(
          `${API}/five-pillars-hub/meetings/${activeMeeting.id}/notes`,
          {
            content: newNote,
            page_id: activeMeeting.current_page,
            pillar_id: currentPage?.pillar_id || null,
            is_task: true,
            task_id: taskRes.data.id
          },
          { headers: getHeaders() }
        );
        
        toast.success('Task created and synced to your profile!');
      } else {
        // Regular note/action/decision/blocker
        await axios.post(
          `${API}/five-pillars-hub/meetings/${activeMeeting.id}/notes`,
          {
            content: newNote,
            page_id: activeMeeting.current_page,
            pillar_id: currentPage?.pillar_id || null,
            is_action_item: noteType === 'action',
            is_decision: noteType === 'decision',
            is_blocker: noteType === 'blocker'
          },
          { headers: getHeaders() }
        );
        toast.success('Note added');
      }
      
      setNewNote('');
      setNoteType('note');
      
      // Refresh meeting data
      const meetingRes = await axios.get(
        `${API}/five-pillars-hub/meetings/${activeMeeting.id}`,
        { headers: getHeaders() }
      );
      setActiveMeeting(meetingRes.data.meeting);
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Failed to add ' + (noteType === 'task' ? 'task' : 'note'));
    }
  };

  // Convert action to task
  const convertToTask = async (actionId, actionContent) => {
    if (!activeMeeting) return;
    
    try {
      await axios.post(
        `${API}/five-pillars-hub/meetings/${activeMeeting.id}/action-items/${actionId}/convert-to-task`,
        { title: actionContent },
        { headers: getHeaders() }
      );
      
      toast.success('Task created!');
      
      // Refresh meeting
      const meetingRes = await axios.get(
        `${API}/five-pillars-hub/meetings/${activeMeeting.id}`,
        { headers: getHeaders() }
      );
      setActiveMeeting(meetingRes.data.meeting);
    } catch (error) {
      console.error('Error converting to task:', error);
      toast.error('Failed to create task');
    }
  };

  // Toggle task status (bi-directional sync)
  const toggleMeetingTask = async (taskId, currentStatus) => {
    if (!activeMeeting) return;
    
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    
    try {
      await axios.patch(
        `${API}/five-pillars-hub/meetings/${activeMeeting.id}/tasks/${taskId}`,
        { status: newStatus },
        { headers: getHeaders() }
      );
      
      toast.success(newStatus === 'completed' ? 'Task completed!' : 'Task reopened');
      
      // Refresh meeting
      const meetingRes = await axios.get(
        `${API}/five-pillars-hub/meetings/${activeMeeting.id}`,
        { headers: getHeaders() }
      );
      setActiveMeeting(meetingRes.data.meeting);
    } catch (error) {
      console.error('Error toggling task:', error);
      toast.error('Failed to update task');
    }
  };

  // Finish meeting
  const finishMeeting = async () => {
    if (!activeMeeting) return;
    
    try {
      await axios.post(
        `${API}/five-pillars-hub/meetings/${activeMeeting.id}/finish`,
        { wrap_up_notes: '' },
        { headers: getHeaders() }
      );
      
      toast.success('Meeting finished!');
      setActiveMeeting(null);
      if (fetchData) fetchData();
    } catch (error) {
      console.error('Error finishing meeting:', error);
      toast.error('Failed to finish meeting');
    }
  };

  // Get current page index
  const getCurrentPageIndex = () => {
    if (!activeMeeting) return 0;
    return MEETING_PAGES.findIndex(p => p.id === activeMeeting.current_page);
  };

  // Navigate to next/prev page
  const goToNextPage = () => {
    const currentIndex = getCurrentPageIndex();
    if (currentIndex < MEETING_PAGES.length - 1) {
      navigateMeeting(MEETING_PAGES[currentIndex + 1].id);
    }
  };

  const goToPrevPage = () => {
    const currentIndex = getCurrentPageIndex();
    if (currentIndex > 0) {
      navigateMeeting(MEETING_PAGES[currentIndex - 1].id);
    }
  };

  // Refresh meeting data
  const refreshMeeting = useCallback(async (meetingId) => {
    const idToUse = meetingId || activeMeeting?.id;
    if (!idToUse) return;
    try {
      const meetingRes = await axios.get(
        `${API}/five-pillars-hub/meetings/${idToUse}`,
        { headers: getHeaders() }
      );
      setActiveMeeting(meetingRes.data.meeting);
    } catch (error) {
      console.error('Error refreshing meeting:', error);
    }
  }, [activeMeeting]);

  return {
    // State
    activeMeeting,
    setActiveMeeting,
    meetings,
    setMeetings,
    showMeetingDialog,
    setShowMeetingDialog,
    meetingNotes,
    setMeetingNotes,
    newNote,
    setNewNote,
    noteType,
    setNoteType,
    
    // Actions
    startMeeting,
    navigateMeeting,
    addMeetingNote,
    convertToTask,
    toggleMeetingTask,
    finishMeeting,
    refreshMeeting,
    
    // Navigation helpers
    getCurrentPageIndex,
    goToNextPage,
    goToPrevPage,
    
    // Constants
    MEETING_PAGES
  };
};

export default useMeetingManagement;
